import * as React from 'react';

import { Badge, Button, cn, Email, LocalizedPhone } from '@rematter/ui-web';
import { LucideDot, LucidePencil, LucideTrash2 } from 'lucide-react';

import { ContactRecord } from '../../_graphql/queryAccounts';
import { useTranslations } from '../../i18n/useTranslations';

type ContactCardProps = {
  contact: ContactRecord;
  onEdit?: (contact: ContactRecord) => void;
  onDelete?: (contact: ContactRecord) => void;
};

export const ContactCard = ({
  contact,
  onEdit,
  onDelete,
}: ContactCardProps) => {
  const t = useTranslations();

  const [isDeleting, setIsDeleting] = React.useState(false);

  const handleDelete = async () => {
    setIsDeleting(true);
    onDelete?.(contact);
  };

  return (
    <div
      className={
        '@ContactCard border-neutral/10 not-last:border-b not-last:pb-3'
      }
    >
      <div
        className={cn(
          'grid grid-cols-[260px_auto_120px] gap-2',
          isDeleting && 'pointer-events-none opacity-30 grayscale',
        )}
      >
        <div>
          <div className="flex items-center gap-2 font-medium">
            {[contact.prefix, contact.fullName].filter(Boolean).join(' ')}
            {contact.isPrimary && (
              <Badge color="success">
                {t.accountsView.form.fields.contacts.primaryBadge}
              </Badge>
            )}
          </div>
          <div className="flex text-xs opacity-50">
            {contact.role}
            <LucideDot size={16} />
            {contact.department}
          </div>
        </div>
        <div>
          <Email>{contact.email}</Email>
          <LocalizedPhone>{contact.phone}</LocalizedPhone>
        </div>
        <div className="flex justify-end gap-1">
          {onEdit && (
            <Button
              icon={<LucidePencil />}
              variant="outline"
              onClick={() => onEdit?.(contact)}
              preventFocus
            />
          )}
          {onDelete && (
            <Button
              icon={<LucideTrash2 />}
              color="failure"
              variant="outline"
              isDisabled={contact.isPrimary}
              onClick={handleDelete}
              preventFocus
            />
          )}
        </div>
      </div>
      {contact.notes && (
        <div
          className={cn(
            'mt-1 inline-flex rounded bg-neutral/5 p-1.5 px-2 text-xs text-neutral/60',
            isDeleting && 'pointer-events-none opacity-30',
          )}
        >
          {contact.notes}
        </div>
      )}
    </div>
  );
};
